import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { SliEvents } from '../../metrics/sliConstants';
import { resolvePubnubError, sleep } from './utils';

const LOG = LoggerFactory.create('PubNubHistory');

// the max number of messages to be fetched in one history request.
// if no max results specified, default to 100.
const MAX_COUNT = 100;
const MAX_RETRIES = 3;
const RETRY_DELAY = 500;

/**
 * method that gets messages for a channel by calling the history api.
 * @param {Object} param object containing attributes required for calling the history api.
 * @returns {Object} object containing messages, channel and end timetoken.
 */
export const getMessagesForChannel = async ({
  pubNubClient,
  channel,
  start,
  count = MAX_COUNT,
  retry = 0,
  delay = RETRY_DELAY
}) => {
  const results = [];
  const returnValue = {
    channel,
    start: start || null,
    messages: []
  };
  const nextRequest = {
    start,
    pubNubClient,
    channel,
    retry
  };
  try {
    if (retry > 0) {
      // added exponential delay before retrying
      await sleep(retry * delay);
    }
    const response = await pubNubClient.fetchMessages({
      channels: [channel],
      start,
      stringifiedTimeToken: true,
      count
    });

    const messages = response?.channels[channel] || [];
    if (messages?.length > 0) {
      const nextStartTimeToken = messages[0]?.timetoken;
      results.push(...messages);
      nextRequest.start = nextStartTimeToken;
      returnValue.start = nextStartTimeToken;
    }
    if (messages?.length === count) {
      const nextResponse = await getMessagesForChannel(nextRequest);
      const { messages: messagesToAdd } = nextResponse;
      if (messagesToAdd.length > 0) {
        results.push(...messagesToAdd);
      }
    }
    return { ...returnValue, messages: results };
  } catch (err) {
    const message = `Error in pubNub history call for channel ${channel}, start ${start}`;

    const errorData = resolvePubnubError(err);
    if (retry < MAX_RETRIES) {
      LOG.debug(`${message}. Retrying with count...${retry + 1}`, errorData);
      return getMessagesForChannel({
        pubNubClient,
        channel,
        start,
        count,
        retry: retry + 1,
        delay: RETRY_DELAY
      });
    }
    LOG.error(`${message}. Retry exhausted!`, errorData);
    throw err;
  }
};

/**
 * returns array of messages with ChannelName added to them
 * @param {Object} param0 object containing messages array and channelName to be added
 */
export const messagesWithChannelName = ({ messages, channel }) => {
  return messages.map(announcement => {
    const { message, timetoken } = announcement;
    return {
      channelName: channel,
      timetoken,
      entry: message
    };
  });
};

export const getAndFormatMessagesForChannel = async ({
  channel,
  pubNubClient
}) => {
  try {
    const response = await getMessagesForChannel({
      pubNubClient,
      channel
    });

    return messagesWithChannelName(response);
  } catch (e) {
    LOG.error('Error while fetching or formatting messages', e);
    throw e;
  }
};

/**
 * method to return all messages for the channels and update the channel timeToken corresponding to a channel in pubNub document.
 * @param {Object} param object containing channels, eventId, attendeeId and pubNub instance to fetch messages
 */
export const getMessagesForChannels = async ({
  attendeeId,
  eventId,
  channels,
  pubNubClient,
  successCallback,
  trackAction
}) => {
  try {
    const response = await Promise.all(
      channels.map(async channel =>
        getAndFormatMessagesForChannel({
          channel,
          pubNubClient
        })
      )
    );
    // track when fetching messages is success
    trackAction(SliEvents.NOTIFICATION_PUBNUB_FETCH_MESSAGES, {
      result: 'success',
      channels
    });
    // to get 1D array
    const messages = response.flat();
    if (messages?.length === 0) {
      return [];
    }
    const successResponse = await successCallback({
      messages,
      eventId,
      attendeeId
    });
    return successResponse;
  } catch (e) {
    const errorData = resolvePubnubError(e);
    const errorMessage = `Failed to fetch messages eventId: ${eventId}, attendeeId: ${attendeeId}`;
    trackAction(SliEvents.NOTIFICATION_PUBNUB_FETCH_MESSAGES, {
      result: 'error',
      channels,
      error: errorMessage,
      ...errorData
    });
    LOG.error(errorMessage, errorData);
    throw e;
  }
};
